import { useEffect, useState } from "react";
import { CheckCircle2, Lock, Mail, X } from "lucide-react";
import { apiSend } from "../services/api";

export function NewsletterModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    // Check if dismissed or subscribed previously
    const dismissedUntil = localStorage.getItem("zimsmart_newsletter_dismissed_until");
    const subscribed = localStorage.getItem("zimsmart_newsletter_subscribed");

    if (subscribed === "true") return;
    if (dismissedUntil && Number(dismissedUntil) > Date.now()) return;

    let activityTimer: NodeJS.Timeout | null = null;
    let activityStarted = false;
    let secondsActive = 0;

    // Timer that ticks when user is active
    const interval = setInterval(() => {
      if (activityStarted) {
        secondsActive += 1;
        if (secondsActive >= 30) {
          triggerModal();
          clearInterval(interval);
        }
      }
    }, 1000);

    const onUserActivity = () => {
      activityStarted = true;
    };

    // Exit intent detection (mouse leaving window via top)
    const onMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 10 && secondsActive >= 5) {
        triggerModal();
      }
    };

    window.addEventListener("mousemove", onUserActivity, { passive: true });
    window.addEventListener("scroll", onUserActivity, { passive: true });
    window.addEventListener("keydown", onUserActivity, { passive: true });
    document.addEventListener("mouseleave", onMouseLeave);

    function triggerModal() {
      // Re-verify conditions
      const currentDismissed = localStorage.getItem("zimsmart_newsletter_dismissed_until");
      if (currentDismissed && Number(currentDismissed) > Date.now()) return;
      if (localStorage.getItem("zimsmart_newsletter_subscribed") === "true") return;

      setIsOpen(true);
      window.removeEventListener("mousemove", onUserActivity);
      window.removeEventListener("scroll", onUserActivity);
      window.removeEventListener("keydown", onUserActivity);
      document.removeEventListener("mouseleave", onMouseLeave);
      if (interval) clearInterval(interval);
    }

    return () => {
      if (interval) clearInterval(interval);
      window.removeEventListener("mousemove", onUserActivity);
      window.removeEventListener("scroll", onUserActivity);
      window.removeEventListener("keydown", onUserActivity);
      document.removeEventListener("mouseleave", onMouseLeave);
    };
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        dismissModal(7); // Dismiss for 7 days
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  const dismissModal = (days = 7) => {
    setIsOpen(false);
    const expiry = Date.now() + days * 24 * 60 * 60 * 1000;
    localStorage.setItem("zimsmart_newsletter_dismissed_until", String(expiry));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      setErrorMessage("Please enter a valid email address");
      return;
    }

    setStatus("loading");
    setErrorMessage("");

    try {
      await apiSend("/api/newsletter/subscribe", {
        method: "POST",
        body: JSON.stringify({
          email: email.trim(),
          name: name.trim() || undefined,
          source: "modal_exit_intent"
        })
      });

      setStatus("success");
      localStorage.setItem("zimsmart_newsletter_subscribed", "true");
      setTimeout(() => {
        setIsOpen(false);
      }, 3000);
    } catch (err: any) {
      setStatus("error");
      setErrorMessage(err.message || "Could not complete subscription. Please try again.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="mag-modal-backdrop" onClick={() => dismissModal(3)}>
      <div
        className="mag-modal-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="newsletter-modal-title"
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={() => dismissModal(7)}
          className="mag-modal-close-btn"
          aria-label="Close newsletter modal"
        >
          <X size={18} />
        </button>

        {status === "success" ? (
          <div className="mag-modal-success-view">
            <div className="mag-modal-success-icon">
              <CheckCircle2 size={44} className="text-emerald-600" />
            </div>
            <h3 className="mag-modal-success-title">Welcome to the Newsroom Dispatch</h3>
            <p className="mag-modal-success-desc">
              Your email <strong>{email}</strong> has been added to our morning editorial list. Look
              out for tomorrow's first briefing.
            </p>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="mag-modal-primary-btn"
            >
              Continue Reading ZimSmart
            </button>
          </div>
        ) : (
          <div className="mag-modal-form-view">
            <div className="mag-modal-header">
              <div className="mag-modal-brand-row">
                <span className="mag-modal-brand">ZIMSMART</span>
                <span className="mag-modal-badge">
                  <Mail size={11} className="text-amber-500" />
                  <span>MORNING DISPATCH</span>
                </span>
              </div>

              <h2 id="newsletter-modal-title" className="mag-modal-heading">
                Essential Zimbabwe Intelligence, Delivered Daily.
              </h2>
              <p className="mag-modal-subheading">
                Join 18,500+ decision-makers, executives, and tech founders receiving our curated
                morning brief on fintech, energy, markets, and policy.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="mag-modal-form">
              {errorMessage && <div className="mag-modal-error">{errorMessage}</div>}

              <div className="mag-modal-fields">
                <div className="mag-modal-field">
                  <label htmlFor="modal-name">Your Name (Optional)</label>
                  <input
                    id="modal-name"
                    type="text"
                    placeholder="e.g. Tendai Moyo"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={status === "loading"}
                  />
                </div>

                <div className="mag-modal-field">
                  <label htmlFor="modal-email">Work or Personal Email</label>
                  <div className="relative">
                    <input
                      id="modal-email"
                      type="email"
                      required
                      placeholder="tendai@company.co.zw"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={status === "loading"}
                      autoFocus
                    />
                    <Mail
                      size={16}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={status === "loading"}
                className="mag-modal-primary-btn"
              >
                {status === "loading" ? "Adding to Editorial List..." : "Get Tomorrow's Morning Briefing"}
              </button>

              <div className="mag-modal-footer-notes">
                <div className="flex items-center justify-center gap-1 text-slate-500 text-xs">
                  <Lock size={11} />
                  <span>Zero spam. Unsubscribe in 1 click at any time.</span>
                </div>
                <button
                  type="button"
                  onClick={() => dismissModal(14)}
                  className="mag-modal-dismiss-link"
                >
                  No thanks, I'll read on the site
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
